import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useGaTracking from 'hooks/use-ga-tracking';
import Heading from 'components/heading';
import { cookieConsentFor } from 'js/cookie-consent';
import { getUTMParameters } from 'js/utm-helper';
import { BrregInputSearchPair } from './BrregInputSearchPair.js';

import cn from 'classnames';

const CognitoForm = ({ dataKey, dataForm, title, autoFillFields, darkTheme }) => {
  const cognitoScriptSrc = 'https://www.cognitoforms.com/f/seamless.js';
  const formRef = useRef(null);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    const UTMParameters = getUTMParameters();
    const prefillForm = () => {
      if (window.Cognito && typeof Cognito.prefill === 'function') {
        Cognito.prefill({ UTMParameters: UTMParameters });
      }
    };

    const script = document.createElement('script');
    script.setAttribute('src', cognitoScriptSrc);
    script.setAttribute('data-key', dataKey);
    script.setAttribute('data-form', dataForm);
    script.setAttribute('type', 'text/javascript');
    script.async = true;
    script.onload = prefillForm;

    if (formRef.current) {
      formRef.current.appendChild(script);
    }

    const setupEventHandlers = () => {
      if (window.Cognito) {
        window.Cognito.on('beforeSubmit', event => {
          const { entry, hasErrors } = event.data;

          if (hasErrors) return;

          if (cookieConsentFor('analytics_storage') && window.dataLayer) {
            const personIdentificators = {
              Name: entry?.Name,
              Email: entry?.Email,
              PhoneNumber: entry?.PhoneNumber
            };

            window.dataLayer.push({
              event: 'formSubmit',
              formTitle: title,
              personIdentificators: personIdentificators,
              UTMParameters: UTMParameters
            });

            useGaTracking('CognitoForm', 'Form Submit', title);
          }
        });
      }
    };

    // Cognito Forms renders some label texts after the <form> appears, so a
    // field lookup can fail on the first try; retry setup until it succeeds.
    const autoFillRetryDelayMs = 500;
    const maxAutoFillAttempts = 20;
    const initializedAutoFills = new Set();

    const SetupAutoFillFields = attempt => {
      let failed = false;

      for (const aff of autoFillFields) {
        if (aff.handlerType != BrregInputSearchPair.HandlerType) continue;
        if (initializedAutoFills.has(aff)) continue;

        try {
          new BrregInputSearchPair(formRef.current, aff);
          initializedAutoFills.add(aff);
        } catch (e) {
          failed = true;
          if (attempt >= maxAutoFillAttempts)
            console.warn('Brreg autofill setup failed:', e);
        }
      }

      if (failed && attempt < maxAutoFillAttempts) {
        window.setTimeout(
          () => SetupAutoFillFields(attempt + 1),
          autoFillRetryDelayMs
        );
        return;
      }

      window.addEventListener('click', ev =>
        BrregInputSearchPair.DeleteAllDropDownOptions(ev)
      );

      window.addEventListener('keydown', ev =>
        BrregInputSearchPair.HandleKeyDown(ev)
      );
    };

    const observer = new MutationObserver(() => {
      if (window.Cognito) {
        setupEventHandlers();
        observer.disconnect();
      }
    });

    observer.observe(formRef.current, { childList: true, subtree: true });

    const observeAndFillFields = new MutationObserver(() => {
      if (
        formRef != null &&
        formRef.current != null &&
        formRef.current.querySelector('form')
      ) {
        observeAndFillFields.disconnect();

        window.setTimeout(() => SetupAutoFillFields(1), 500);
      }
    });

    observeAndFillFields.observe(formRef.current, {
      childList: true,
      subtree: true
    });

    return () => {
      observer.disconnect();
      if (formRef.current?.contains(script)) {
        formRef.current.removeChild(script);
      }
    };
  }, [dataKey, dataForm, title]);

  return (
    <div className={cn("cognito-form", { 'cognito-form--dark-theme': darkTheme })}>
      <Heading
        level={2}
        theme={Heading.themes.h2}
        className="cognito-form__title"
      >
        {title}
      </Heading>
      <div ref={formRef} className='cognito-form__container'></div>
    </div>
  );
};

CognitoForm.propTypes = {
  dataKey: PropTypes.string.isRequired,
  dataForm: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  autoFillFields: PropTypes.arrayOf(
    PropTypes.exact({
      handlerType: PropTypes.string.isRequired,
      inputFieldName: PropTypes.string.isRequired,
      apiEndpoint: PropTypes.string.isRequired,
      localityEndpoint: PropTypes.string.isRequired,
      targetFieldName: PropTypes.string.isRequired,
      postNrFieldName: PropTypes.string,
      postStedFieldName: PropTypes.string,
      noResultsFoundText: PropTypes.string,
      didYouMeanText: PropTypes.string
    })
  ),
  darkTheme: PropTypes.bool
};

export default CognitoForm;
